import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import {
  OrganizationAffiliation,
  AffiliationsResponse,
  CreateAffiliationRequest,
  UpdateAffiliationRequest,
} from 'src/@types/events_org';

// Helper function to convert Firestore timestamps to dates
const convertTimestamps = (data: any): any => {
  if (!data) return data;
  const converted = { ...data };
  if (converted.createdAt instanceof Timestamp) {
    converted.createdAt = converted.createdAt.toDate();
  }
  if (converted.updatedAt instanceof Timestamp) {
    converted.updatedAt = converted.updatedAt.toDate();
  }
  if (converted.joinedAt instanceof Timestamp) {
    converted.joinedAt = converted.joinedAt.toDate();
  }
  return converted;
};

// ==================== AFFILIATIONS API ====================

export const affiliationsApi = createApi({
  reducerPath: 'affiliationsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Affiliation', 'Affiliations', 'SupraOrg'],
  endpoints: (builder) => ({
    /**
     * Get all affiliations for a supra organization
     */
    getAffiliationsByOrganization: builder.query<AffiliationsResponse, string>({
      async queryFn(supraOrgId) {
        try {
          const affiliationsRef = collection(DB, 'organisers', supraOrgId, 'affiliations');
          const q = query(affiliationsRef, orderBy('affiliatedOrgName', 'asc'));

          const snapshot = await getDocs(q);
          const affiliations: OrganizationAffiliation[] = snapshot.docs.map((d) => ({
            id: d.id,
            ...convertTimestamps(d.data()),
          })) as OrganizationAffiliation[];

          return { data: { affiliations, total: affiliations.length } };
        } catch (error: any) {
          console.error('Error fetching affiliations:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      providesTags: ['Affiliations'],
    }),

    /**
     * Get the supra org info for a child organization
     * Returns the parent supra org if the current org has supraOrgId set
     */
    getSupraOrgForOrganization: builder.query<
      { supraOrg: { id: string; name: string; logoImage?: string } | null },
      string
    >({
      async queryFn(orgId) {
        try {
          const orgRef = doc(DB, 'organisers', orgId);
          const orgDoc = await getDoc(orgRef);

          if (!orgDoc.exists()) {
            return { data: { supraOrg: null } };
          }

          const orgData = orgDoc.data();
          if (!orgData?.supraOrgId) {
            return { data: { supraOrg: null } };
          }

          const supraOrgRef = doc(DB, 'organisers', orgData.supraOrgId);
          const supraOrgDoc = await getDoc(supraOrgRef);

          if (!supraOrgDoc.exists()) {
            return { data: { supraOrg: null } };
          }

          const supraOrgData = supraOrgDoc.data();
          return {
            data: {
              supraOrg: {
                id: supraOrgDoc.id,
                name: supraOrgData?.name || '',
                logoImage: supraOrgData?.logoImage,
              },
            },
          };
        } catch (error: any) {
          console.error('Error fetching supra org:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, orgId) => [{ type: 'SupraOrg', id: orgId }],
    }),

    /**
     * Create a new affiliation (link a child org to a supra org)
     * Uses batch write for atomicity: creates affiliation doc + sets child's supraOrgId
     */
    createAffiliation: builder.mutation<
      { affiliation: OrganizationAffiliation },
      CreateAffiliationRequest
    >({
      async queryFn(data) {
        try {
          const { supraOrgId, affiliatedOrgId } = data;

          // Validate: cannot affiliate self
          if (supraOrgId === affiliatedOrgId) {
            return {
              error: { status: 'VALIDATION_ERROR', error: 'Cannot affiliate an organization with itself' },
            };
          }

          // Validate: check target org exists and is Evenementiel
          const targetOrgRef = doc(DB, 'organisers', affiliatedOrgId);
          const targetOrgDoc = await getDoc(targetOrgRef);

          if (!targetOrgDoc.exists()) {
            return { error: { status: 'NOT_FOUND', error: 'Target organization not found' } };
          }

          const targetOrgData = targetOrgDoc.data();

          if (targetOrgData?.type && targetOrgData.type !== 'Evenementiel') {
            return {
              error: {
                status: 'VALIDATION_ERROR',
                error: 'Only event organizations can be affiliated',
              },
            };
          }

          // Validate: target must not already be a child of another supra org
          if (targetOrgData?.supraOrgId) {
            return {
              error: {
                status: 'VALIDATION_ERROR',
                error: 'This organization is already affiliated with another supra organization',
              },
            };
          }

          const batch = writeBatch(DB);

          // Create affiliation document in subcollection
          const affiliationsRef = collection(DB, 'organisers', supraOrgId, 'affiliations');
          const newAffiliationRef = doc(affiliationsRef);

          const affiliationData = {
            supraOrgId,
            affiliatedOrgId: data.affiliatedOrgId,
            affiliatedOrgName: data.affiliatedOrgName,
            affiliatedOrgLogo: data.affiliatedOrgLogo || null,
            affiliationType: data.affiliationType,
            status: 'active' as const,
            joinedAt: serverTimestamp(),
            approvedBy: data.approvedBy || null,
            sharePublicEvents: true,
            shareStatistics: false,
            useBranding: false,
            notes: data.notes || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          batch.set(newAffiliationRef, affiliationData);

          // Set supraOrgId on the child org
          batch.update(targetOrgRef, { supraOrgId });

          await batch.commit();

          // Read back the created affiliation
          const createdDoc = await getDoc(newAffiliationRef);
          const affiliation: OrganizationAffiliation = {
            id: createdDoc.id,
            ...convertTimestamps(createdDoc.data()),
          } as OrganizationAffiliation;

          return { data: { affiliation } };
        } catch (error: any) {
          console.error('Error creating affiliation:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['Affiliations'],
    }),

    /**
     * Update an existing affiliation's metadata
     */
    updateAffiliation: builder.mutation<
      { affiliation: OrganizationAffiliation },
      UpdateAffiliationRequest
    >({
      async queryFn({ supraOrgId, affiliationId, updates }) {
        try {
          const affiliationRef = doc(
            DB,
            'organisers',
            supraOrgId,
            'affiliations',
            affiliationId
          );

          await updateDoc(affiliationRef, {
            ...updates,
            updatedAt: serverTimestamp(),
          });

          const updatedDoc = await getDoc(affiliationRef);
          const affiliation: OrganizationAffiliation = {
            id: updatedDoc.id,
            ...convertTimestamps(updatedDoc.data()),
          } as OrganizationAffiliation;

          return { data: { affiliation } };
        } catch (error: any) {
          console.error('Error updating affiliation:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { affiliationId }) => [
        { type: 'Affiliation', id: affiliationId },
        'Affiliations',
      ],
    }),

    /**
     * Remove an affiliation (unlink a child org from a supra org)
     * Uses batch write: deletes affiliation doc + clears child's supraOrgId
     */
    removeAffiliation: builder.mutation<
      null,
      { supraOrgId: string; affiliationId: string; affiliatedOrgId: string }
    >({
      async queryFn({ supraOrgId, affiliationId, affiliatedOrgId }) {
        try {
          const batch = writeBatch(DB);

          // Delete the affiliation document
          const affiliationRef = doc(
            DB,
            'organisers',
            supraOrgId,
            'affiliations',
            affiliationId
          );
          batch.delete(affiliationRef);

          // Clear supraOrgId on the child org
          const childOrgRef = doc(DB, 'organisers', affiliatedOrgId);
          batch.update(childOrgRef, { supraOrgId: deleteField() });

          await batch.commit();

          return { data: null };
        } catch (error: any) {
          console.error('Error removing affiliation:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['Affiliations'],
    }),

    /**
     * Toggle supra org mode on/off for an organization
     */
    toggleSupraOrg: builder.mutation<null, { orgId: string; isSupraOrg: boolean }>({
      async queryFn({ orgId, isSupraOrg }) {
        try {
          const orgRef = doc(DB, 'organisers', orgId);

          if (isSupraOrg) {
            // Enable supra org mode
            await updateDoc(orgRef, { isSupraOrg: true, updatedAt: serverTimestamp() });
          } else {
            // Disable supra org mode — also clean up all affiliations
            const affiliationsRef = collection(DB, 'organisers', orgId, 'affiliations');
            const snapshot = await getDocs(affiliationsRef);

            const batch = writeBatch(DB);

            // Remove supraOrgId from each child org and delete affiliation docs
            for (const affiliationDoc of snapshot.docs) {
              const affiliationData = affiliationDoc.data();
              if (affiliationData?.affiliatedOrgId) {
                const childOrgRef = doc(DB, 'organisers', affiliationData.affiliatedOrgId);
                batch.update(childOrgRef, { supraOrgId: deleteField() });
              }
              batch.delete(affiliationDoc.ref);
            }

            // Disable supra org flag
            batch.update(orgRef, {
              isSupraOrg: deleteField(),
              updatedAt: serverTimestamp(),
            });

            await batch.commit();
          }

          return { data: null };
        } catch (error: any) {
          console.error('Error toggling supra org:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['Affiliations', 'SupraOrg'],
    }),
  }),
});

export const {
  useGetAffiliationsByOrganizationQuery,
  useGetSupraOrgForOrganizationQuery,
  useCreateAffiliationMutation,
  useUpdateAffiliationMutation,
  useRemoveAffiliationMutation,
  useToggleSupraOrgMutation,
} = affiliationsApi;
